import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { generateTaxFormPDF } from "./pdfGenerator";
import { getQuestions } from "./questionDataService";
import companyLogo from "../../Assets/BooksCompl.png";
import PDFPreviewModal from "./PDFPreviewModal";
import { FaTimes, FaChevronDown, FaChevronUp, FaUpload, FaFile, FaTrash, FaSave, FaCheck, FaSpinner } from "react-icons/fa";

const QuestionsForm = ({
  onClose, onSubmit, initialAnswers, initialComments, initialFiles = [], isSubmitted, formNumber, memberType = "primary", memberIndex = 0, memberName = ""
}) => {
  const MAX_FILES = 20;
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  const [files, setFiles] = useState(() => {
    console.log('QuestionsForm initialFiles:', initialFiles);
    // Convert stored file data to the format expected by the component
    if (initialFiles && initialFiles.length > 0) {
      const convertedFiles = initialFiles.map(file => {
        // If it's already in the correct format (has file property), keep it
        if (file.file) {
          return file;
        }
        // If it's stored file data (has url property), convert it
        if (file.url) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url,
            isStored: true // Flag to indicate this is a stored file
          };
        }
        return file;
      });
      console.log('QuestionsForm converted files:', convertedFiles);
      return convertedFiles;
    }
    return [];
  });
  const [uploadProgress] = useState({});
  const [formQuestions, setFormQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [previewPDFBlob, setPreviewPDFBlob] = useState(null);
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const questions = await getQuestions(formNumber);
        setFormQuestions(questions);
        setError('');
      } catch (err) {
        setError('Failed to load questions. Please try again.');
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [formNumber]);

  // Debug effect to monitor initialFiles changes
  useEffect(() => {
    console.log('QuestionsForm initialFiles changed:', initialFiles);
    if (initialFiles && initialFiles.length > 0) {
      const convertedFiles = initialFiles.map(file => {
        console.log('Processing file:', file);
        // If it's already in the correct format (has file property), keep it
        if (file.file) {
          return file;
        }
        // If it's stored file data (has url property), convert it
        if (file.url) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url,
            isStored: true // Flag to indicate this is a stored file
          };
        }
        return file;
      });
      console.log('Setting files to:', convertedFiles);
      setFiles(convertedFiles);
    } else {
      console.log('No initialFiles, setting files to empty array');
      setFiles([]);
    }
  }, [initialFiles]);

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length,
    0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined).length;
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined).length;
  };

  const handleFileChange = (e) => {
    if (!isSubmitted && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const currentFileCount = files.length;
      const availableSlots = MAX_FILES - currentFileCount;

      if (newFiles.length > availableSlots) {
        alert(`You can only upload up to ${MAX_FILES} files. You currently have ${currentFileCount} files and tried to add ${newFiles.length} more files.`);
        return;
      }

      const formattedNewFiles = newFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0
      }));

      setFiles(prev => [...prev, ...formattedNewFiles]);
      e.target.value = '';
    }
  };

  const removeFile = (index) => {
    if (!isSubmitted) {
      setFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (!isSubmitted) {
      const currentProgress = calculateProgress();
      
      // Convert files to the format expected by the backend
      const formattedFiles = files.map(file => {
        if (file.isStored) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url
          };
        }
        return {
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url || ''
        };
      });
      
      onSubmit({ answers, comments, files: formattedFiles }, false, memberType, memberIndex);
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (totalQuestions === 0) {
      alert("Questions are still loading. Please wait a moment before submitting.");
      return;
    }
    if (currentProgress < totalQuestions) {
      alert(`Please answer all ${totalQuestions} checklist questions before submitting.`);
      return;
    }

    if (!isSubmitted) {
      try {
        // Step 1: Upload files first
        let fileUrls = [];
        if (files.length > 0) {
          const newFiles = files.filter(file => file.file && !file.isStored);
          const storedFiles = files.filter(file => file.isStored);
          
          if (newFiles.length > 0) {
            const formData = new FormData();
            newFiles.forEach(file => {
              formData.append('files', file.file);
            });

            const token = localStorage.getItem("token");
            const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files`, {
              method: "POST",
              body: formData,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!uploadResponse.ok) {
              throw new Error("Failed to upload files");
            }

            const uploadResult = await uploadResponse.json();
            fileUrls = uploadResult.fileUrls || [];
          }
          
          storedFiles.forEach(file => {
            fileUrls.push(file.url);
          });
        }

        // Step 2: Generate PDF preview (without signature)
        const pdfBlob = generateTaxFormPDF(
          formQuestions,
          answers,
          comments,
          formNumber,
          companyLogo,
          memberType,
          memberIndex,
          null // No signature data yet
        );

        // Store uploaded file URLs and show PDF preview
        setUploadedFileUrls(fileUrls);
        setPreviewPDFBlob(pdfBlob);
        setShowPDFPreview(true);
      } catch (error) {
        console.error("Error preparing submission:", error);
        alert(`Failed to prepare submission: ${error.message}`);
      }
    }
  };

  const handlePDFPreviewConfirm = async (signatureData) => {
    setIsSubmitting(true);
    try {
      // Generate final PDF with signature
      const signedPDFBlob = generateTaxFormPDF(
        formQuestions,
        answers,
        comments,
        formNumber,
        companyLogo,
        memberType,
        memberIndex,
        signatureData
      );

      // Upload signed PDF
      const pdfFormData = new FormData();
      const currentYear = new Date().getFullYear();
      let pdfName = `Info.Checklist filled ${currentYear}.pdf`;
      if (memberType !== "primary") {
        pdfName = `Info.Checklist filled ${currentYear} (${memberName || `Member ${memberIndex + 1}`}).pdf`;
      }
      pdfFormData.append('pdf', signedPDFBlob, pdfName);

      const pdfToken = localStorage.getItem("token");
      const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
        method: "POST",
        body: pdfFormData,
        headers: {
          Authorization: `Bearer ${pdfToken}`,
        },
      });

      if (!pdfResponse.ok) {
        throw new Error("Failed to upload signed PDF");
      }

      const pdfResult = await pdfResponse.json();

      // Prepare submitted files data
      const submittedFiles = files.map(file => {
        if (file.isStored && file.url) {
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url
          };
        }
        
        const matchingUrl = Array.isArray(uploadedFileUrls)
          ? uploadedFileUrls.find(url => typeof url === 'string' && url.includes(encodeURIComponent(file.name)))
          : '';

        return {
          name: file.name,
          size: file.size,
          type: file.type,
          url: matchingUrl || ''
        };
      });

      // Submit with signature data
      await onSubmit({
        answers,
        comments,
        pdfUrl: pdfResult.pdfUrl,
        fileUrls: uploadedFileUrls,
        files: submittedFiles,
        signatureData
      }, true, memberType, memberIndex);

      // Close modal first
      setShowPDFPreview(false);
      
      // Small delay before showing success message to ensure modal is closed
      setTimeout(() => {
        alert("Form submitted successfully!");
      }, 300);
    } catch (error) {
      console.error("Error in final submission:", error);
      alert(`Failed to submit form: ${error.message}`);
      setShowPDFPreview(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!modalRoot) {
    return null;
  }

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading questions...</p>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  if (error) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <FaTimes className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button 
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  const getOptionStyles = (option, isSelected) => {
    if (!isSelected) return 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600';
    switch (option) {
      case 'yes': return 'bg-green-500 text-white shadow-lg shadow-green-500/30';
      case 'no': return 'bg-red-500 text-white shadow-lg shadow-red-500/30';
      case 'na': return 'bg-gray-500 text-white shadow-lg shadow-gray-500/30';
      case 'comment': return 'bg-primary-500 text-white shadow-lg shadow-primary-500/30';
      default: return 'bg-gray-100 dark:bg-dark-700';
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-dark-800 dark:text-white">
              Initial Information (Checklist)
            </h2>
            <div className="flex items-center gap-3">
              <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button 
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30">
            <div className="space-y-2 text-sm text-primary-700 dark:text-primary-300">
              <p><span className="font-semibold">1.</span> Please upload documents in Part A.</p>
              <p><span className="font-semibold">2.</span> Parts B, C etc. will be completed by our team for your review and confirmation.</p>
            </div>
          </div>
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            return (  
              <div key={index} className="rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm">
                {/* Section Header */}
                <button
                  onClick={() => toggleQuestion(questionTitle)}
                  className="w-full flex items-center justify-between p-4 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-primary-500/30">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-dark-800 dark:text-white text-left text-base">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-dark-600 text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-dark-500">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-dark-600'}`}>
                      {isExpanded ? <FaChevronUp className="w-4 h-4 text-primary-500" /> : <FaChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>
                </button>
                
                {/* Section Content */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-gray-50 dark:bg-dark-800 border-t border-gray-200 dark:border-dark-600">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => (
                      <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 shadow-sm">
                        <p className="text-dark-800 dark:text-gray-200 mb-4 leading-relaxed">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-semibold text-xs mr-2">{subIndex + 1}</span>
                          {subQuestion}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {["yes", "no", "na", "comment"].map((option) => (
                            <button
                              key={option}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnswer(`${questionTitle}-${subIndex}`, option);
                              }}
                              disabled={isSubmitted}
                              className={`px-5 py-2 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getOptionStyles(option, answers[`${questionTitle}-${subIndex}`] === option)}`}
                            >
                              {option.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        {answers[`${questionTitle}-${subIndex}`] === "comment" && (
                          <textarea
                            placeholder="Add a comment..."
                            value={comments[`${questionTitle}-${subIndex}`] || ""}
                            onChange={(e) => handleComment(`${questionTitle}-${subIndex}`, e.target.value)}
                            disabled={isSubmitted}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-4 w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                            rows={3}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* File Upload Section */}
        <div className="px-6 pb-4">
          {/* Small Upload Button */}
          <label className={`inline-flex items-center gap-2 py-2 px-4 rounded-full border transition-all duration-200 cursor-pointer text-sm ${isSubmitted ? 'border-gray-300 dark:border-dark-600 bg-gray-100 dark:bg-dark-700/50 cursor-not-allowed' : 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:border-primary-500'}`}>
            <input
              type="file"
              onChange={handleFileChange}
              disabled={isSubmitted}
              multiple
              className="hidden"
            />
            <FaUpload className={`w-4 h-4 ${isSubmitted ? 'text-gray-400' : 'text-primary-500'}`} />
            <span className={`font-medium ${isSubmitted ? 'text-gray-400' : 'text-primary-600 dark:text-primary-400'}`}>
              {files.length}/{MAX_FILES} files selected
            </span>
          </label>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5">
              {files.map((file, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FaFile className="w-4 h-4 text-primary-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-dark-800 dark:text-white truncate" title={file.name}>
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  {!isSubmitted && (
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1.5 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                      title="Remove file"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button 
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
          >
            Close
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={handleSave}
              disabled={isSubmitted}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <FaSave className="w-4 h-4" />
              Save Progress
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitted}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <FaCheck className="w-4 h-4" />
              Confirm & Submit
            </button>
          </div>
        </div>
      </div>
      
      {/* PDF Preview Modal */}
      {showPDFPreview && previewPDFBlob && (
        <PDFPreviewModal
          pdfBlob={previewPDFBlob}
          onClose={() => setShowPDFPreview(false)}
          onConfirm={handlePDFPreviewConfirm}
          isSubmitting={isSubmitting}
        />
      )}
    </div>,
    modalRoot
  );
};

export default QuestionsForm;